/**
 * AvatarUpload - Avatar upload component with preview and drag & drop
 *
 * Features:
 * - Drag & drop file upload
 * - Image preview before upload
 * - File validation (type, size)
 * - Upload progress indicator
 * - Replace/Remove actions
 */
import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { Avatar } from '../common/Avatar';
import { getCachebustedAvatarUrl } from '../../utils/avatarCache';
import './AvatarUpload.css';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

const AvatarUpload = ({ user, onAvatarChange }) => {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  /**
   * Validate file before upload
   */
  const validateFile = (file) => {
    if (!file) {
      toast.error('No file selected');
      return false;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPG, or WEBP image.');
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`);
      return false;
    }

    return true;
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = (file) => {
    if (!validateFile(file)) return;

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    uploadAvatar(file);
  };

  /**
   * Upload avatar to backend
   */
  const uploadAvatar = async (file) => {
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/profile/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Avatar uploaded successfully!');

      // Update parent component with cache-busted avatar URL to avoid browser cache issues
      if (onAvatarChange) {
        const cacheBustedUrl = getCachebustedAvatarUrl(response.data.avatar_url);
        onAvatarChange(cacheBustedUrl);
      }

      setPreview(null);
    } catch (error) {
      console.error('Avatar upload failed:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to upload avatar';
      toast.error(errorMsg);
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  /**
   * Delete avatar
   */
  const handleDeleteAvatar = async () => {
    if (!user?.avatar_url) return;

    if (!window.confirm('Are you sure you want to remove your avatar?')) {
      return;
    }

    try {
      await api.delete('/profile/avatar');
      toast.success('Avatar removed successfully');

      // Update parent component - pass null to hide avatar
      if (onAvatarChange) {
        onAvatarChange(null);
      }
    } catch (error) {
      console.error('Avatar deletion failed:', error);
      toast.error('Failed to remove avatar');
    }
  };

  /**
   * Handle drag events
   */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  /**
   * Handle file drop
   */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  /**
   * Handle file input change
   */
  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  /**
   * Trigger file input click
   */
  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="avatar-upload-container">
      <div className="avatar-upload-preview">
        {preview ? (
          <div className="avatar-preview-loading">
            <img src={preview} alt="Preview" className="avatar-preview-image" />
            {uploading && (
              <div className="avatar-upload-overlay">
                <div className="avatar-upload-spinner" />
                <p>Uploading...</p>
              </div>
            )}
          </div>
        ) : (
          <Avatar user={user} size="xl" />
        )}
      </div>

      <div className="avatar-upload-actions" style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '8px' }}>
        {/* File Input (Hidden) */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          onChange={handleChange}
          style={{ display: 'none' }}
        />

        {/* Upload/Replace Button */}
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleButtonClick}
          disabled={uploading}
        >
          {user?.avatar_url ? 'Replace' : 'Upload'} Avatar
        </button>

        {/* Remove Button */}
        {user?.avatar_url && !uploading && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleDeleteAvatar}
          >
            Remove
          </button>
        )}
      </div>

      {/* Drag & Drop Zone */}
      <div
        className={`avatar-dropzone ${dragActive ? 'active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <p className="dropzone-text">
          Drag and drop your image here
          <br />
          <span className="dropzone-hint">or click "Upload Avatar" button</span>
        </p>
        <p className="dropzone-requirements">
          PNG, JPG, or WEBP • Max 2MB • Will be resized to 256x256
        </p>
      </div>
    </div>
  );
};

export default AvatarUpload;
